import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronDown, ChevronRight, RefreshCw, AlertTriangle, FileText, Network, Server, Shield, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useToast } from '@/contexts/ToastContext'
import { engagementsApi, type EngagementAsset, type EngagementDetail, type EngagementEvidence, type EngagementFinding, type EngagementPort, type EngagementSession, type TopologySnapshot } from '@/lib/engagementsApi'
import { TopologyView, type TopologyViewHandle } from '@/components/pentest/TopologyView'
import { classifySubnets, synthesizeAttackEvidence, synthesizeAttackFindings, synthesizeAttackTargetAssets } from '@/components/pentest/topologyModes/topologyAggregates'
import { evidenceHostIp, findingHostIp, humanEvidenceType, isSnmpEvidence, isSnmpFinding, snmpEvidenceKeySummary } from '@/components/pentest/snmpEvidence'
import { serviceEvidenceKeySummary } from '@/components/pentest/serviceEvidence'
import { NodeDetailDrawer } from '@/components/pentest/NodeDetailDrawer'
import { CanvasToolbar } from '@/components/pentest/CanvasToolbar'
import { TopologyToolbar, type TopologyMode } from '@/components/pentest/TopologyToolbar'
import { PortSpeechBubble, type PortInfo } from '@/components/pentest/PortSpeechBubble'

// reduced top-level nav: Overview / Assets / Evidence / Simulations.
// Assets folds the old Topology + Hosts tabs. Evidence folds Findings +
// Evidence. Simulations is the renamed Attacks tab
type TabKey      = 'overview' | 'assets' | 'evidence' | 'simulations'
// sub-mode under each composite tab. persist across tab swaps so a user
// who set Assets to "Hosts" sees it again on return
type AssetsMode  = 'topology' | 'hosts'
type EvidenceMode = 'findings' | 'evidence'

// map legacy tab keys (used in shared links and older docs) to their new
// home. anything not in the map falls back to 'overview'
const LEGACY_TAB_ALIASES: Record<string, { tab: TabKey; assets?: AssetsMode; evidence?: EvidenceMode }> = {
  overview:  { tab: 'overview' },
  topology:  { tab: 'assets',      assets:   'topology' },
  hosts:     { tab: 'assets',      assets:   'hosts'    },
  assets:    { tab: 'assets' },
  findings:  { tab: 'evidence',    evidence: 'findings' },
  evidence:  { tab: 'evidence',    evidence: 'evidence' },
  attacks:   { tab: 'simulations' },
  simulations: { tab: 'simulations' },
}

const fmtDateTime = (s: string | null): string => {
  if (!s) return '—'
  try { return new Date(s).toLocaleString() } catch { return s }
}

const fmtDuration = (started: string | null, ended: string | null): string => {
  if (!started || !ended) return '—'
  try {
    const ms = new Date(ended).getTime() - new Date(started).getTime()
    if (!isFinite(ms) || ms < 0) return '—'
    const min = Math.floor(ms / 60000)
    if (min < 60) return `${min}m`
    const h = Math.floor(min / 60)
    const r = min % 60
    return r === 0 ? `${h}h` : `${h}h ${r}m`
  } catch { return '—' }
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const tone = status === 'submitted'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${tone}`}>
      {status}
    </span>
  )
}

// parse legacy ?tab= or #tab= deep-links into the new compound state.
// only runs at mount. subsequent in-app nav just calls
// setActiveTab/setAssetsMode/setEvidenceMode without touching the URL
function readInitialTab(): { tab: TabKey; assets?: AssetsMode; evidence?: EvidenceMode } {
  if (typeof window === 'undefined') return { tab: 'overview' }
  const search = new URLSearchParams(window.location.search)
  const hash   = window.location.hash.replace(/^#/, '')
  const hashParams = new URLSearchParams(hash.includes('=') ? hash : `tab=${hash}`)
  const raw = (search.get('tab') ?? hashParams.get('tab') ?? '').toLowerCase()
  if (raw && LEGACY_TAB_ALIASES[raw]) return LEGACY_TAB_ALIASES[raw]
  return { tab: 'overview' }
}

const Cell: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-xs text-slate-700 dark:text-slate-200 break-words">{value}</span>
  </div>
)

export const EngagementDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { error: showError } = useToast()
  const { t } = useTranslation()

  const [detail,  setDetail]  = useState<EngagementDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedNode, setSelectedNode] = useState<Record<string, any> | null>(null)
  // tab + sub-mode state. the hash deep-link form (#tab=topology) is
  // honoured once on mount so legacy bookmarks keep working
  const [activeTab,    setActiveTab]    = useState<TabKey>(() => readInitialTab().tab)
  const [assetsMode,   setAssetsMode]   = useState<AssetsMode>(() => readInitialTab().assets ?? 'topology')
  const [evidenceMode, setEvidenceMode] = useState<EvidenceMode>(() => readInitialTab().evidence ?? 'findings')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const canvasRef = useMemo(() => ({ current: null as TopologyViewHandle | null }), [])

  useEffect(() => {
    if (!isFullscreen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isFullscreen])

  const load = async () => {
    if (!id) return
    setLoading(true)
    try {
      setDetail(await engagementsApi.detail(id))
    } catch (err: any) {
      showError(t('pentest.detail.loadFailed', 'Failed to load engagement'), err?.message)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load()   }, [id])

  const portsByAsset = useMemo(() => {
    const m = new Map<string, EngagementPort[]>()
    if (!detail) return m
    for (const p of detail.ports) {
      if (!p.asset_id) continue
      const arr = m.get(p.asset_id) ?? []
      arr.push(p)
      m.set(p.asset_id, arr)
    }
    return m
  }, [detail])

  // set of IPs targeted by any attack run. drives the Hosts-tab status
  // filter ("Attacked") and the Summary/Attack mode host filters below
  const attackedIpsSet = useMemo(() => {
    const s = new Set<string>()
    if (!detail) return s
    for (const r of detail.attack_runs) {
      if (r.target_ip) s.add(r.target_ip)
    }
    return s
  }, [detail])

  // display-merged detail: real DB rows + client-side fallbacks for gaps
  // Dorothy currently leaves.
  //
  // always:
  //  - asset list falls back to topology nodes when engagement_assets is
  //    empty (mirrors the Hosts-tab logic so Topology and Hosts agree on
  //    host counts)
  //  - SNMP findings with an empty evidence_ids[] get auto-linked to every
  //    SNMP evidence row in the same engagement so the drawer's "Linked
  //    evidence" + Hosts-tab finding links resolve
  //  - SNMP findings with an unresolved asset get patched with an asset
  //    stub derived from linked-evidence target IP
  //
  // synth-only (no DB row equivalent):
  //  - phantom assets for attack target IPs missing from engagement_assets
  //  - attack-run evidence + findings when Dorothy hasn't shipped
  //    normalized counterparts
  const displayDetail = useMemo<EngagementDetail | null>(() => {
    if (!detail) return null
    const baseAssets = detail.assets.length > 0
      ? detail.assets
      : deriveAssetsFromTopology(detail.topology_snapshot)
    const synthAssets    = synthesizeAttackTargetAssets(baseAssets, detail.attack_runs)
    const mergedAssets   = synthAssets.length > 0 ? [...baseAssets, ...synthAssets] : baseAssets
    const synthEvidence  = synthesizeAttackEvidence(detail.attack_runs, detail.evidence, mergedAssets)
    const synthFindings  = synthesizeAttackFindings(detail.attack_runs, detail.findings, mergedAssets)
    const allEvidence    = synthEvidence.length > 0 ? [...detail.evidence, ...synthEvidence] : detail.evidence
    const snmpEvidenceIds = allEvidence.filter(isSnmpEvidence).map(ev => ev.id)
    // patch each finding: auto-attach SNMP evidence + resolve host IP
    const assetsByIp = new Map(mergedAssets.map(a => [a.host_ip, a]))
    const patchedFindings = [...detail.findings, ...synthFindings].map(f => {
      const out: any = { ...f }
      // auto-link SNMP findings → SNMP evidence when the finding came in
      // with an empty evidence_ids list
      const hasNoLinks = !Array.isArray(out.evidence_ids) || out.evidence_ids.length === 0
      if (hasNoLinks && isSnmpFinding(out, allEvidence) && snmpEvidenceIds.length > 0) {
        out.evidence_ids = snmpEvidenceIds
      }
      // resolve an asset if missing, try the host-IP fallback chain
      if (!out.asset) {
        const ip = findingHostIp(out, allEvidence)
        const a = ip ? assetsByIp.get(ip) : null
        if (a) {
          out.asset_id = out.asset_id ?? a.id
          out.asset = {
            id: a.id,
            host_ip: a.host_ip,
            host_name: a.host_name,
            subnet_cidr: a.subnet_cidr,
            device_type: a.device_type,
          }
        } else if (ip) {
          // no asset row exists yet (Dorothy didn't ship asset rows).
          // attach a host_ip-only stub so the Hosts-with-findings bucket
          // still picks it up
          out.asset = { id: '', host_ip: ip, host_name: null, subnet_cidr: null, device_type: null }
        }
      }
      return out
    })
    // same trick for evidence asset resolution
    const patchedEvidence = allEvidence.map(ev => {
      if (ev.asset) return ev
      const ip = evidenceHostIp(ev)
      if (!ip) return ev
      const a = assetsByIp.get(ip)
      return a
        ? { ...ev, asset_id: ev.asset_id ?? a.id, asset: { id: a.id, host_ip: a.host_ip, host_name: a.host_name, subnet_cidr: a.subnet_cidr, device_type: a.device_type } }
        : { ...ev, asset: { id: '', host_ip: ip, host_name: null, subnet_cidr: null, device_type: null } }
    })
    return {
      ...detail,
      assets:   mergedAssets,
      evidence: patchedEvidence,
      findings: patchedFindings,
    }
  }, [detail])

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 px-4 py-6">
        <div className="mx-auto max-w-[1400px] text-sm text-slate-400">
          {t('common.loading', 'Loading…')}
        </div>
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 px-4 py-6">
        <div className="mx-auto max-w-[1400px] text-sm text-slate-500">
          {t('pentest.detail.notFound', 'Engagement not found.')}{' '}
          <Link to="/pentest-history" className="text-blue-600 hover:underline">
            {t('pentest.detail.backToList', 'Back to list')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">

      {/* header + tab bar, capped at content width so the tab bar stays
          visually anchored even when the topology tab body escapes the
          cap below */}
      <div className="mx-auto max-w-[1400px] px-4 pt-4">

        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <button
            onClick={() => navigate('/pentest-history')}
            className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex-shrink-0"
            title={t('pentest.detail.backToList', 'Back to list') as string}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-slate-400">
              <span className="hover:underline cursor-pointer text-slate-500" onClick={() => navigate('/dashboard')}>
                {t('nav.dashboard', 'Dashboard')}
              </span>
              <span> &rsaquo; </span>
              <span className="hover:underline cursor-pointer text-slate-500" onClick={() => navigate('/pentest-history')}>
                {t('pentest.history.title', 'Pentest History')}
              </span>
              <span> &rsaquo; </span>
              <span>{t('pentest.detail.crumb', 'Engagement')}</span>
            </div>
            <h1 className="text-lg font-semibold mt-0.5 leading-tight">{detail.name}</h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusBadge status={detail.status} />
            <button
              onClick={load}
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              title={t('common.refresh', 'Refresh') as string}
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* tab bar, four top-level tabs. Hosts + Topology live under
            Assets. Findings + Evidence live under Evidence. Attacks is now
            "Simulations". counts mirror the underlying data so a quick
            glance still hints at what's inside */}
        <div className="border-b border-slate-200 mb-4 -mt-2">
          <nav className="flex gap-1" role="tablist">
            {(() => {
              const assetCount = detail.assets.length > 0
                ? detail.assets.length
                : deriveAssetsFromTopology(detail.topology_snapshot).length
              const findingCount  = displayDetail?.findings.length ?? 0
              const evidenceCount = displayDetail?.evidence.length ?? 0
              const tabs: { key: TabKey; label: string; count?: number }[] = [
                { key: 'overview',    label: t('pentest.detail.tabs.overview',    'Overview') },
                { key: 'assets',      label: t('pentest.detail.tabs.assets',      'Assets'),
                  count: assetCount },
                { key: 'evidence',    label: t('pentest.detail.tabs.evidence',    'Evidence'),
                  count: findingCount + evidenceCount },
                { key: 'simulations', label: t('pentest.detail.tabs.simulations', 'Simulations'),
                  count: detail.attack_runs.length },
              ]
              return tabs.map(tab => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  onClick={() => {
                    setActiveTab(tab.key)
                    setSelectedNode(null)
                  }}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  {tab.label}
                  {!!tab.count && <span className="ml-1.5 text-[10px] text-slate-400">({tab.count})</span>}
                </button>
              ))
            })()}
          </nav>
        </div>

      </div>

      {/* tab content. Assets escapes the 1400px cap (its Topology sub-mode
          needs the full viewport width for the canvas). other tabs stay
          capped because forms/tables benefit from a readable line length.
          both keep px-4 gutters */}
      {activeTab === 'assets' ? (
        <div className="px-4 pb-4">
          <AssetsTab
            mode={assetsMode}
            onChangeMode={setAssetsMode}
            detail={detail}
            displayDetail={displayDetail}
            portsByAsset={portsByAsset}
            attackedIps={attackedIpsSet}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen(v => !v)}
            onNodeSelect={setSelectedNode}
            canvasRef={canvasRef}
          />
        </div>
      ) : (
        <div className="mx-auto max-w-[1400px] px-4 pb-4">
          {activeTab === 'overview' && (
            <OverviewTab
              detail={displayDetail ?? detail}
              onJumpTab={setActiveTab}
              onSelectNode={setSelectedNode}
            />
          )}

          {activeTab === 'evidence' && (
            <EvidenceWorkspace
              mode={evidenceMode}
              onChangeMode={setEvidenceMode}
              findings={displayDetail?.findings ?? detail.findings}
              evidence={displayDetail?.evidence ?? detail.evidence}
              subnets={detail.subnets}
              assets={displayDetail?.assets ?? (detail.assets.length > 0
                ? detail.assets
                : deriveAssetsFromTopology(detail.topology_snapshot))}
              onSelect={setSelectedNode}
            />
          )}

          {activeTab === 'simulations' && (
            <AttacksSection
              attackRuns={detail.attack_runs}
              engagementId={detail.id}
              onSelect={setSelectedNode}
            />
          )}
        </div>
      )}

      <NodeDetailDrawer
        nodeData={selectedNode}
        onClose={() => setSelectedNode(null)}
        evidenceList={displayDetail?.evidence ?? detail.evidence}
        findingsList={displayDetail?.findings ?? detail.findings}
        onSelectEvidence={(ev) => setSelectedNode({ kind: 'evidence', ...ev })}
        onReactionSaved={() => { void load() }}
      />
    </div>
  )
}

// Phase 2C-C: pull device classification from topology_snapshot.nodes by
// host_ip lookup. used by the row expander until backlog 29.5 wires the NI
// Asset writer (then the row reads directly from engagement_assets.device_*
// columns instead).
//
// topology nodes carry both camelCase and snake_case mirror keys (Dorothy
// Phase 2B). we read snake_case to match server contract
interface DeviceClassification {
  device_type:             string | null
  device_type_confidence:  string | null
  device_vendor:           string | null
  device_model:            string | null
  device_firmware_version: string | null
}

function lookupDeviceInfo(
  snapshot: TopologySnapshot | null,
  hostIp: string,
): DeviceClassification | null {
  if (!snapshot || !hostIp) return null
  const node = snapshot.nodes.find(n =>
    n.data && (n.data as any).ip === hostIp && n.data.type === 'Host'
  )
  if (!node) return null
  const d = node.data as any
  const hasAny =
    d.device_type || d.device_type_confidence ||
    d.device_vendor || d.device_model || d.device_firmware_version
  if (!hasAny) return null
  return {
    device_type:             d.device_type ?? null,
    device_type_confidence:  d.device_type_confidence ?? null,
    device_vendor:           d.device_vendor ?? null,
    device_model:            d.device_model ?? null,
    device_firmware_version: d.device_firmware_version ?? null,
  }
}

// Phase 2C-C.4: derive Host-type asset rows from topology_snapshot.nodes
// when engagement_assets is empty (Dorothy ships assets:[] until backlog
// 29.5 lands the NI Asset writer). returns the same shape HostsSection
// consumes so the rest of the table renders unchanged.
//
// synthesized rows have stable IDs (`topo:<ip>`) so React keys don't
// collide with real asset IDs (UUIDs)
function deriveAssetsFromTopology(
  snapshot: TopologySnapshot | null,
): EngagementAsset[] {
  if (!snapshot) return []
  const hosts = snapshot.nodes.filter(n =>
    n.data?.type === 'Host' && typeof (n.data as any).ip === 'string'
  )
  return hosts.map(n => {
    const d = n.data as any
    return {
      id:                      `topo:${d.ip}`,
      host_ip:                 d.ip,
      host_name:               d.hostname ?? null,
      mac_address:             d.mac ?? null,
      vendor:                  d.vendor ?? null,
      is_online:               d.stale === true || d.stale === 'true' ? false : true,
      ping_time:               null,
      scan_time:               d.lastProbedAt ?? null,
      industrial_vendor:       null,
      industrial_category:     null,
      industrial_protocols:    null,
      hardware_id:             null,
      machine_name:            null,
      device_type:             d.device_type ?? d.deviceType ?? null,
      device_type_confidence:  d.device_type_confidence ?? d.deviceTypeConfidence ?? null,
      device_vendor:           d.device_vendor ?? d.deviceVendor ?? null,
      device_model:            d.device_model ?? d.deviceModel ?? null,
      device_firmware_version: d.device_firmware_version ?? d.deviceFirmwareVersion ?? null,
      // topology-synthesized rows don't carry the new migration-016
      // columns. Hosts tab fallback derives /24 from host_ip directly
      subnet_cidr:             d.subnet ?? d.subnet_cidr ?? null,
      discovery_source:        null,
      created_at:              '',
    }
  })
}

// derive /24 from an IPv4 host_ip for the Subnet column fallback when
// migration-016 columns are NULL (legacy rows / topology-synthesized)
const subnetOf = (ip: string | null | undefined): string | null => {
  if (!ip) return null
  const parts = ip.split('.')
  if (parts.length !== 4) return null
  if (parts.some(p => !/^\d+$/.test(p))) return null
  return `${parts[0]}.${parts[1]}.${parts[2]}.0/24`
}

const ALL = '__all__'

const HostsSection: React.FC<{
  assets: EngagementDetail['assets']
  portsByAsset: Map<string, EngagementPort[]>
  topologySnapshot: TopologySnapshot | null
  attackedIps?: Set<string>
  findings?: EngagementFinding[]
  evidence?: EngagementEvidence[]
  onSelectFinding?: (data: Record<string, any> | null) => void
}> = ({ assets, portsByAsset, topologySnapshot, attackedIps, findings = [], evidence = [], onSelectFinding }) => {
  const { t } = useTranslation()
  const [expandedAssetId, setExpandedAssetId] = useState<string | null>(null)

  // per-asset finding / evidence counts so the row can show badges without
  // a per-row scan. linked by asset_id then by host_ip fallback
  const findingsByHostKey = useMemo(() => {
    const m = new Map<string, EngagementFinding[]>()
    for (const f of findings) {
      const keys = [f.asset_id, f.asset?.host_ip].filter(Boolean) as string[]
      for (const k of keys) {
        const list = m.get(k) ?? []
        list.push(f)
        m.set(k, list)
      }
    }
    return m
  }, [findings])
  const evidenceByHostKey = useMemo(() => {
    const m = new Map<string, EngagementEvidence[]>()
    for (const ev of evidence) {
      const keys = [ev.asset_id, ev.asset?.host_ip].filter(Boolean) as string[]
      for (const k of keys) {
        const list = m.get(k) ?? []
        list.push(ev)
        m.set(k, list)
      }
    }
    return m
  }, [evidence])
  const [activeBubble, setActiveBubble] = useState<{ info: PortInfo; el: HTMLElement } | null>(null)
  const [subnetFilter, setSubnetFilter] = useState<string>(ALL)
  const [deviceFilter, setDeviceFilter] = useState<string>(ALL)
  const [riskFilter,   setRiskFilter]   = useState<string>(ALL)

  useEffect(() => {
    if (!activeBubble) return
    const onClickAway = (e: MouseEvent) => {
      const bubble = document.querySelector('[data-port-bubble]')
      if (bubble?.contains(e.target as Node)) return
      if (activeBubble.el.contains(e.target as Node)) return
      setActiveBubble(null)
    }
    document.addEventListener('mousedown', onClickAway)
    return () => document.removeEventListener('mousedown', onClickAway)
  }, [activeBubble])

  // compute facets from the full asset list. filters narrow the rendered
  // rows but the dropdowns always show every available value so the user
  // can recover from an over-zealous filter
  const facets = useMemo(() => {
    const subnetSet = new Set<string>()
    const deviceSet = new Set<string>()
    let hasOnlineSignal = false
    let hasRiskSignal   = false
    for (const a of assets) {
      const s = a.subnet_cidr ?? subnetOf(a.host_ip)
      if (s) subnetSet.add(s)
      if (a.device_type) deviceSet.add(a.device_type)
      if (a.is_online !== null) hasOnlineSignal = true
      if (attackedIps?.has(a.host_ip)) hasRiskSignal = true
    }
    return {
      subnets:        Array.from(subnetSet).sort(),
      deviceTypes:    Array.from(deviceSet).sort(),
      hasOnlineSignal,
      hasRiskSignal,
    }
  }, [assets, attackedIps])

  const filteredAssets = useMemo(() => assets.filter(a => {
    if (subnetFilter !== ALL) {
      const s = a.subnet_cidr ?? subnetOf(a.host_ip)
      if (s !== subnetFilter) return false
    }
    if (deviceFilter !== ALL && a.device_type !== deviceFilter) return false
    if (riskFilter === 'attacked' && !(attackedIps?.has(a.host_ip))) return false
    if (riskFilter === 'online'   && a.is_online !== true)            return false
    if (riskFilter === 'offline'  && a.is_online !== false)           return false
    return true
  }), [assets, subnetFilter, deviceFilter, riskFilter, attackedIps])

  if (assets.length === 0) {
    return (
      <section className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-3 mb-4">
        <h2 className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
          {t('pentest.detail.hosts.title', 'Hosts')} (0)
        </h2>
        <p className="text-xs text-slate-500">{t('pentest.detail.hosts.empty', 'No hosts captured.')}</p>
      </section>
    )
  }

  const SelectCls = 'text-[11px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5 text-slate-700 dark:text-slate-200'

  return (
    <section className="rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden mb-4">
      <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-2 justify-between bg-slate-50 dark:bg-slate-800/50">
        <h2 className="text-[10px] uppercase tracking-wide text-slate-400">
          {t('pentest.detail.hosts.title', 'Hosts')} ({filteredAssets.length}{filteredAssets.length !== assets.length ? ` / ${assets.length}` : ''})
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {facets.subnets.length > 1 && (
            <label className="text-[10px] text-slate-400 inline-flex items-center gap-1">
              {t('pentest.detail.hosts.filter.subnet', 'Subnet')}
              <select className={SelectCls} value={subnetFilter} onChange={e => setSubnetFilter(e.target.value)}>
                <option value={ALL}>{t('pentest.detail.hosts.filter.all', 'All')}</option>
                {facets.subnets.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          )}
          {facets.deviceTypes.length > 0 && (
            <label className="text-[10px] text-slate-400 inline-flex items-center gap-1">
              {t('pentest.detail.hosts.filter.device', 'Device')}
              <select className={SelectCls} value={deviceFilter} onChange={e => setDeviceFilter(e.target.value)}>
                <option value={ALL}>{t('pentest.detail.hosts.filter.all', 'All')}</option>
                {facets.deviceTypes.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}
          {(facets.hasOnlineSignal || facets.hasRiskSignal) && (
            <label className="text-[10px] text-slate-400 inline-flex items-center gap-1">
              {t('pentest.detail.hosts.filter.status', 'Status')}
              <select className={SelectCls} value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
                <option value={ALL}>{t('pentest.detail.hosts.filter.all', 'All')}</option>
                {facets.hasRiskSignal   && <option value="attacked">Attacked</option>}
                {facets.hasOnlineSignal && <option value="online">Online</option>}
                {facets.hasOnlineSignal && <option value="offline">Offline</option>}
              </select>
            </label>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-slate-900/40">
            <tr className="text-left text-slate-500 dark:text-slate-400">
              <th className="w-8 px-2 py-1.5"></th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.hosts.col.ip', 'IP')}</th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.hosts.col.hostname', 'Hostname')}</th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.hosts.col.subnet', 'Subnet')}</th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.hosts.col.mac', 'MAC')}</th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.hosts.col.vendor', 'Vendor')}</th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.hosts.col.device', 'Device')}</th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.hosts.col.online', 'Online')}</th>
              <th className="px-2 py-1.5 font-medium text-right">{t('pentest.detail.hosts.col.ports', 'Ports')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
            {filteredAssets.map(a => {
              const ports = portsByAsset.get(a.id) ?? []
              const isExpanded = expandedAssetId === a.id
              const deviceInfo = lookupDeviceInfo(topologySnapshot, a.host_ip)
              const myFindings = (findingsByHostKey.get(a.id) ?? findingsByHostKey.get(a.host_ip) ?? [])
              const myEvidence = (evidenceByHostKey.get(a.id) ?? evidenceByHostKey.get(a.host_ip) ?? [])
              const canExpand = ports.length > 0 || deviceInfo !== null || myFindings.length > 0 || myEvidence.length > 0
              const subnetCidr = a.subnet_cidr ?? subnetOf(a.host_ip)
              return (
                <React.Fragment key={a.id}>
                  <tr
                    onClick={() => canExpand && setExpandedAssetId(isExpanded ? null : a.id)}
                    className={`hover:bg-slate-50 dark:hover:bg-slate-700/40 ${canExpand ? 'cursor-pointer' : ''}`}
                  >
                    <td className="px-2 py-1.5 text-slate-400">
                      {canExpand && (isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />)}
                    </td>
                    <td className="px-2 py-1.5 font-mono">{a.host_ip}</td>
                    <td className="px-2 py-1.5">{a.host_name ?? '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px] text-slate-500">{subnetCidr ?? '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px]">{a.mac_address ?? '—'}</td>
                    <td className="px-2 py-1.5">{a.vendor ?? '—'}</td>
                    <td className="px-2 py-1.5">
                      {deviceInfo?.device_type ? (
                        <span>
                          {deviceInfo.device_type}
                          {deviceInfo.device_type_confidence && (
                            <span className="text-slate-400 ml-1 text-[10px]">({deviceInfo.device_type_confidence})</span>
                          )}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      {a.is_online == null ? '—' : a.is_online ? (
                        <span className="text-emerald-600 dark:text-emerald-400">●</span>
                      ) : (
                        <span className="text-slate-400">○</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{ports.length}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-slate-50 dark:bg-slate-900/20">
                      <td colSpan={9} className="px-2 py-2">
                        {deviceInfo && (
                          <div className="ml-6 mb-2 text-[11px]">
                            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
                              {t('pentest.detail.hosts.device.title', 'Device classification')}
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              {deviceInfo.device_type && (
                                <span>
                                  <span className="text-slate-400">{t('pentest.detail.hosts.device.type', 'Type')}: </span>
                                  <span className="text-slate-700">{deviceInfo.device_type}</span>
                                  {deviceInfo.device_type_confidence && (
                                    <span className="text-slate-400 ml-1">({deviceInfo.device_type_confidence})</span>
                                  )}
                                </span>
                              )}
                              {deviceInfo.device_vendor && (
                                <span>
                                  <span className="text-slate-400">{t('pentest.detail.hosts.device.vendor', 'Vendor')}: </span>
                                  <span className="text-slate-700">{deviceInfo.device_vendor}</span>
                                </span>
                              )}
                              {deviceInfo.device_model && (
                                <span>
                                  <span className="text-slate-400">{t('pentest.detail.hosts.device.model', 'Model')}: </span>
                                  <span className="text-slate-700">{deviceInfo.device_model}</span>
                                </span>
                              )}
                              {deviceInfo.device_firmware_version && (
                                <span>
                                  <span className="text-slate-400">{t('pentest.detail.hosts.device.firmware', 'Firmware')}: </span>
                                  <span className="text-slate-700">{deviceInfo.device_firmware_version}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {ports.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 ml-6">
                            {ports.map(p => (
                              <button
                                key={p.id}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  const el = e.currentTarget as HTMLElement
                                  const info: PortInfo = {
                                    port:     String(p.port),
                                    protocol: p.protocol ?? 'tcp',
                                    banner:   p.banner,
                                    severity: p.severity,
                                    assetId:  a.id,
                                  }
                                  setActiveBubble({ info, el })
                                }}
                                className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:border-blue-400 hover:text-blue-600"
                                title={p.service ?? ''}
                              >
                                {p.port}/{p.protocol ?? 'tcp'}
                                {p.service && <span className="text-slate-400 ml-1">{p.service}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                        {(myFindings.length > 0 || myEvidence.length > 0) && (
                          <div className="ml-6 mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                            {myFindings.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
                                  {t('pentest.detail.hosts.findings', 'Findings')} ({myFindings.length})
                                </div>
                                <div className="flex flex-col gap-1">
                                  {myFindings.slice(0, 5).map(f => (
                                    <button
                                      key={f.id}
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); onSelectFinding?.({ kind: 'finding', ...f }) }}
                                      className="text-left bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-[11px] hover:border-blue-400"
                                    >
                                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${sevTone(f.severity).dot}`} />
                                      <span className="text-slate-700 dark:text-slate-200">{f.title}</span>
                                      <span className="text-slate-400 ml-1">({f.severity})</span>
                                    </button>
                                  ))}
                                  {myFindings.length > 5 && (
                                    <div className="text-[10px] text-slate-400 italic">+{myFindings.length - 5} more</div>
                                  )}
                                </div>
                              </div>
                            )}
                            {myEvidence.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
                                  {t('pentest.detail.hosts.evidence', 'Evidence')} ({myEvidence.length})
                                </div>
                                <div className="flex flex-col gap-1">
                                  {myEvidence.slice(0, 5).map(ev => (
                                    <button
                                      key={ev.id}
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); onSelectFinding?.({ kind: 'evidence', ...ev }) }}
                                      className="text-left bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-[11px] hover:border-blue-400"
                                    >
                                      <span className="text-[10px] text-slate-500 mr-1">{humanEvidenceType(ev.evidence_type)}</span>
                                      <span className="text-slate-700 dark:text-slate-200">{snmpEvidenceKeySummary(ev) || ev.summary || '(no summary)'}</span>
                                    </button>
                                  ))}
                                  {myEvidence.length > 5 && (
                                    <div className="text-[10px] text-slate-400 italic">+{myEvidence.length - 5} more</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {activeBubble && (
        <PortSpeechBubble portInfo={activeBubble.info} triggerElement={activeBubble.el} />
      )}
    </section>
  )
}

// severity → tone tuple used by Overview + Findings tab
const SEV_LEVELS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'] as const
const sevTone = (s: string): { dot: string; pill: string; text: string } => {
  switch (s.toUpperCase()) {
    case 'CRITICAL': return { dot: 'bg-rose-500',    pill: 'bg-rose-100 text-rose-700',    text: 'text-rose-600' }
    case 'HIGH':     return { dot: 'bg-orange-500',  pill: 'bg-orange-100 text-orange-700', text: 'text-orange-600' }
    case 'MEDIUM':   return { dot: 'bg-amber-400',   pill: 'bg-amber-100 text-amber-700',   text: 'text-amber-600' }
    case 'LOW':      return { dot: 'bg-yellow-300', pill: 'bg-yellow-100 text-yellow-700', text: 'text-yellow-600' }
    case 'INFO':     return { dot: 'bg-sky-300',    pill: 'bg-sky-100 text-sky-700',       text: 'text-sky-600' }
    default:         return { dot: 'bg-slate-300',  pill: 'bg-slate-100 text-slate-600',   text: 'text-slate-600' }
  }
}

const OverviewCard: React.FC<{ label: string; value: React.ReactNode; tone?: 'rose' | 'orange' | 'slate' | 'amber'; icon?: React.ReactNode; onClick?: () => void; tooltip?: string }> = ({ label, value, tone, icon, onClick, tooltip }) => {
  const toneCls = tone === 'rose' ? 'text-rose-600'
                : tone === 'orange' ? 'text-orange-600'
                : tone === 'amber' ? 'text-amber-600'
                : 'text-slate-800 dark:text-slate-100'
  // compact: tighter padding + text-lg value so eight cards fit two rows
  // of four on desktop without dominating the fold
  return (
    <button
      type="button"
      disabled={!onClick}
      onClick={onClick}
      title={tooltip}
      className={`text-left bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2.5 py-2 ${onClick ? 'hover:shadow-md transition-shadow cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400 truncate">
        {icon}{label}
      </div>
      <div className={`text-lg font-semibold tabular-nums mt-0.5 ${toneCls}`}>{value}</div>
    </button>
  )
}

// Overview used to embed a reconstructed mini topology card here. removed
// in favour of the report-style summary panels below. without Dorothy's
// real canvas positions we were just inventing a layout, which read as
// "fake topology" to operators. the full topology workspace lives under
// Assets

const OverviewTab: React.FC<{ detail: EngagementDetail; onJumpTab: (k: TabKey) => void; onSelectNode: (data: Record<string, any> | null) => void }> = ({ detail, onJumpTab, onSelectNode }) => {
  const { t } = useTranslation()

  // per-severity finding tallies, computed once per tab visit
  const sevCounts = useMemo(() => {
    const c = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 } as Record<string, number>
    for (const f of detail.findings) {
      const s = (f.severity ?? '').toUpperCase()
      if (s in c) c[s]++
    }
    return c
  }, [detail.findings])

  // distinct attack-target IPs, drives the "Tested targets" Overview card.
  // count IPs rather than runs so 5 SYN-flood runs against one host show
  // as 1 tested target
  const attackTargetsCount = useMemo(() => {
    const s = new Set<string>()
    for (const r of detail.attack_runs) if (r.target_ip) s.add(r.target_ip)
    return s.size
  }, [detail.attack_runs])

  // quick-list rows for Overview, computed once per detail change.
  //
  // - hostsWithFindings: distinct IPs on >=1 finding, with the worst
  //   severity and a friendly host label. excludes attack-simulation
  //   findings unless impact was recorded (driven by the upstream synth
  //   filter, not here).
  // - simulationTargets: distinct target IPs with run count + attack
  //   types, sorted by run count.
  // - recentEvidence: latest 6 by collected_at desc, falls back to
  //   insertion order when timestamps are absent.
  const hostsWithFindings = useMemo(() => {
    const sevRankOf = (s: string | null | undefined): number =>
      ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].indexOf((s ?? '').toUpperCase())
    // per-IP roll-up, keep the worst finding around so the row click can
    // open its bottom sheet directly
    const byIp = new Map<string, { ip: string; label: string; count: number; sev: string; worst: EngagementFinding }>()
    for (const f of detail.findings) {
      const ip = f.asset?.host_ip ?? null
      if (!ip) continue
      const existing = byIp.get(ip)
      if (!existing) {
        byIp.set(ip, {
          ip,
          label: f.asset?.host_name ?? '',
          count: 1,
          sev:   (f.severity ?? '').toUpperCase(),
          worst: f,
        })
        continue
      }
      existing.count++
      if (sevRankOf(f.severity) >= 0 && sevRankOf(f.severity) < sevRankOf(existing.sev)) {
        existing.sev   = (f.severity ?? '').toUpperCase()
        existing.worst = f
      }
    }
    return Array.from(byIp.values()).sort((a, b) => sevRankOf(a.sev) - sevRankOf(b.sev))
  }, [detail.findings])

  const simulationTargets = useMemo(() => {
    const byIp = new Map<string, { ip: string; runs: number; types: string[] }>()
    for (const r of detail.attack_runs) {
      if (!r.target_ip) continue
      const cur = byIp.get(r.target_ip) ?? { ip: r.target_ip, runs: 0, types: [] }
      cur.runs++
      const type = r.attack_type ?? 'attack'
      if (!cur.types.includes(type)) cur.types.push(type)
      byIp.set(r.target_ip, cur)
    }
    return Array.from(byIp.values()).sort((a, b) => b.runs - a.runs)
  }, [detail.attack_runs])

  const recentEvidence = useMemo(() => {
    const list = [...detail.evidence]
    list.sort((a, b) => {
      const at = a.collected_at ?? ''
      const bt = b.collected_at ?? ''
      return bt.localeCompare(at)
    })
    return list.slice(0, 6)
  }, [detail.evidence])

  // sanitized subnet count. strip /32 route artifacts from the headline
  // number so the Overview shows real subnets, not the union with
  // auto-route noise
  const subnetCounts = useMemo(() => {
    const { display, ignored } = classifySubnets(
      detail.subnets.map(s => ({
        cidr: s.cidr,
        discovery_source: s.discovery_source ?? null,
        hostCount: s.host_count,
        classifiedCount: s.classified_count,
        highRiskCount: s.high_risk_count,
        attackedCount: s.attacked_count,
        findingCount: 0,
        worstSeverity: 'normal' as const,
        hosts: [],
      }))
    )
    return { display: display.length, ignored: ignored.length }
  }, [detail.subnets])

  return (
    <div className="flex flex-col gap-4">
      {/* headline counter cards, compact, 4 per row on desktop. spec rule:
          do not conflate "attack run" with "risky host". tested targets =
          distinct attack target IPs. findings counters drive risk
          semantics */}
      <section>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <OverviewCard label="Hosts"    value={detail.assets.length}      icon={<Server className="h-3 w-3" />} onClick={() => onJumpTab('assets')} />
          <OverviewCard
            label="Subnets"
            value={subnetCounts.display}
            icon={<Network className="h-3 w-3" />}
            onClick={() => onJumpTab('assets')}
            tooltip={subnetCounts.ignored > 0
              ? `${subnetCounts.display} display subnet${subnetCounts.display === 1 ? '' : 's'}, ${subnetCounts.ignored} ignored route artifact${subnetCounts.ignored === 1 ? '' : 's'}`
              : undefined}
          />
          <OverviewCard
            label="Confirmed findings"
            value={detail.findings.length}
            icon={<AlertTriangle className="h-3 w-3" />}
            onClick={() => onJumpTab('evidence')}
            tooltip="Findings derived from a confirmed exposure (e.g. SNMP sensitive community detected). Excludes attack-simulation evidence with no observed impact."
          />
          <OverviewCard label="Critical findings" value={sevCounts.CRITICAL} tone="rose"   icon={<span className="inline-block w-2 h-2 rounded-full bg-rose-500" />} onClick={() => onJumpTab('evidence')} />
          <OverviewCard label="High findings"     value={sevCounts.HIGH}     tone="orange" icon={<span className="inline-block w-2 h-2 rounded-full bg-orange-500" />} onClick={() => onJumpTab('evidence')} />
          <OverviewCard label="Evidence items"    value={detail.evidence.length}    icon={<FileText className="h-3 w-3" />} onClick={() => onJumpTab('evidence')} />
          <OverviewCard label="Attack runs"       value={detail.attack_runs.length} icon={<Zap className="h-3 w-3" />} onClick={() => onJumpTab('simulations')} />
          <OverviewCard
            label="Attack simulation targets"
            value={attackTargetsCount}
            icon={<AlertTriangle className="h-3 w-3" />}
            onClick={() => onJumpTab('simulations')}
            tooltip="Distinct host IPs that received attack simulations. Being tested is not the same as being vulnerable."
          />
        </div>
      </section>

      {/* session + engagement metadata folded into a single collapsible
          card so it doesn't push the counters off the top of the page.
          defaults to closed, expands on click */}
      <details className="rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        <summary className="cursor-pointer select-none px-3 py-2 text-[10px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1.5 hover:bg-slate-50">
          <Shield className="h-3 w-3" />
          {t('pentest.detail.session.summary', 'Session & engagement details')}
          <span className="ml-auto text-[10px] text-slate-400 normal-case font-normal">
            {detail.session?.session_name ?? detail.client_name ?? detail.name}
            {detail.session?.source_nic && ` · NIC ${detail.session.source_nic}`}
          </span>
        </summary>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-3 border-t border-slate-100">
          <div>
            <h3 className="text-[10px] uppercase tracking-wide text-slate-400 mb-2 font-semibold">
              {t('pentest.detail.session.title', 'Session')}
            </h3>
            <SessionPanel session={detail.session} />
          </div>
          <div>
            <h3 className="text-[10px] uppercase tracking-wide text-slate-400 mb-2 font-semibold">
              {t('pentest.detail.engagement.title', 'Engagement')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Cell label={t('pentest.detail.client', 'Client / Vessel')} value={detail.client_name ?? '—'} />
              <Cell label={t('pentest.detail.scope', 'Scope')} value={detail.scope ?? '—'} />
              <Cell label={t('pentest.detail.surveyor', 'Surveyor')} value={
                <span>
                  {detail.surveyor_email ?? '—'}{' '}
                  <span className="font-mono text-[10px] text-slate-400" title={detail.surveyor_hardware_id}>
                    ({detail.surveyor_hardware_id.slice(0, 8)}…)
                  </span>
                </span>
              } />
              <Cell label={t('pentest.detail.started', 'Started')}    value={fmtDateTime(detail.started_at)} />
              <Cell label={t('pentest.detail.ended', 'Ended')}        value={fmtDateTime(detail.ended_at)} />
              <Cell label={t('pentest.detail.duration', 'Duration')}  value={fmtDuration(detail.started_at, detail.ended_at)} />
              <Cell label={t('pentest.detail.submitted', 'Submitted')} value={fmtDateTime(detail.submitted_at)} />
            </div>
            {detail.notes && (
              <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
                  {t('pentest.detail.notes', 'Notes')}
                </div>
                <p className="text-xs whitespace-pre-wrap text-slate-700 dark:text-slate-200">
                  {detail.notes}
                </p>
              </div>
            )}
          </div>
        </div>
      </details>

      {/* quick lists row: hosts with findings | simulation targets |
          recent evidence. per-row clicks open the relevant bottom sheet
          directly so the operator can drill in without a tab hop. header
          click still jumps to the relevant tab for the full list */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <QuickList
          title={t('pentest.detail.overview.findings.hosts', 'Hosts with findings')}
          empty={t('pentest.detail.overview.findings.empty', 'No confirmed findings.')}
          rows={hostsWithFindings.map(h => ({
            key: h.ip,
            tone: h.sev === 'CRITICAL' ? 'rose' : h.sev === 'HIGH' ? 'orange' : 'slate',
            primary: <span className="font-mono">{h.ip}</span>,
            secondary: h.label,
            badge: `${h.count} finding${h.count === 1 ? '' : 's'}`,
            // click opens the worst-severity finding for this host
            onClick: () => onSelectNode({ kind: 'finding', ...h.worst }),
          }))}
          onHeaderClick={() => onJumpTab('evidence')}
        />
        <QuickList
          title={t('pentest.detail.overview.simulations.targets', 'Attack simulation targets')}
          empty={t('pentest.detail.overview.simulations.empty', 'No attack simulations recorded.')}
          rows={simulationTargets.map(s => ({
            key: s.ip,
            tone: 'slate',
            primary: <span className="font-mono">{s.ip}</span>,
            secondary: s.types.join(', '),
            badge: `${s.runs} run${s.runs === 1 ? '' : 's'}`,
            // no per-target bottom sheet today, row click jumps to the
            // Simulations tab where the run table can be filtered
            onClick: () => onJumpTab('simulations'),
          }))}
          onHeaderClick={() => onJumpTab('simulations')}
        />
        <QuickList
          title={t('pentest.detail.overview.evidence.recent', 'Recent evidence')}
          empty={t('pentest.detail.overview.evidence.empty', 'No evidence collected.')}
          rows={recentEvidence.map(ev => ({
            key: ev.id,
            tone: 'slate',
            primary: humanEvidenceType(ev.evidence_type),
            secondary: ev.asset?.host_ip ?? ev.summary ?? '',
            badge: ev.source_tool ?? null,
            // click opens the evidence bottom sheet for this row
            onClick: () => onSelectNode({ kind: 'evidence', ...ev }),
          }))}
          onHeaderClick={() => onJumpTab('evidence')}
        />
      </section>

      {detail.findings.length > 0 && (
        <section className="rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
          <h2 className="text-[10px] uppercase tracking-wide text-slate-400 mb-2 font-semibold">
            {t('pentest.detail.findings.breakdown', 'Severity breakdown')}
          </h2>
          <SeverityBar sevCounts={sevCounts} total={detail.findings.length} />
        </section>
      )}
    </div>
  )
}

// reusable compact list block used by the Overview quick rows. renders up
// to 6 entries. each row carries its own optional onClick (typically opens
// a bottom sheet). the card header is a separate clickable target that
// jumps to the matching top-level tab
const QuickList: React.FC<{
  title: string
  empty: string
  rows: {
    key: string
    tone: 'rose' | 'orange' | 'slate'
    primary: React.ReactNode
    secondary?: React.ReactNode
    badge?: React.ReactNode
    onClick?: () => void
  }[]
  onHeaderClick?: () => void
}> = ({ title, empty, rows, onHeaderClick }) => {
  return (
    <div className="rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
      <button
        type="button"
        onClick={onHeaderClick}
        disabled={!onHeaderClick}
        className={`w-full text-[10px] uppercase tracking-wide text-slate-400 mb-1.5 font-semibold flex items-center justify-between ${onHeaderClick ? 'hover:text-slate-600 cursor-pointer' : 'cursor-default'}`}
      >
        <span>{title}</span>
        {rows.length > 0 && <span className="text-slate-400 normal-case font-normal">{rows.length}</span>}
      </button>
      {rows.length === 0 ? (
        <div className="text-[11px] text-slate-400 italic">{empty}</div>
      ) : (
        <ul className="space-y-1">
          {rows.slice(0, 6).map(r => {
            const toneBorder = r.tone === 'rose' ? 'border-l-rose-400'
                             : r.tone === 'orange' ? 'border-l-orange-400'
                             : 'border-l-slate-200'
            const Wrapper: any = r.onClick ? 'button' : 'li'
            return (
              <Wrapper
                key={r.key}
                type={r.onClick ? 'button' : undefined}
                onClick={r.onClick}
                className={`w-full text-left flex items-center justify-between gap-2 bg-slate-50 dark:bg-slate-900/30 rounded border-l-2 ${toneBorder} px-2 py-1 text-[11px] ${r.onClick ? 'hover:bg-slate-100 cursor-pointer' : ''}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-slate-700 dark:text-slate-200 truncate">{r.primary}</div>
                  {r.secondary && <div className="text-slate-400 truncate">{r.secondary}</div>}
                </div>
                {r.badge && <span className="text-[10px] text-slate-500 flex-shrink-0">{r.badge}</span>}
              </Wrapper>
            )
          })}
        </ul>
      )}
    </div>
  )
}

const SessionPanel: React.FC<{ session: EngagementSession | null }> = ({ session }) => {
  const { t } = useTranslation()
  if (!session) {
    return <p className="text-xs text-slate-400 italic">{t('pentest.detail.session.empty', 'No session metadata submitted.')}</p>
  }
  const subnets = (() => {
    const s = session.selected_subnets
    if (!s) return null
    if (Array.isArray(s)) return s.join(', ')
    try { return JSON.stringify(s) } catch { return String(s) }
  })()
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Cell label={t('pentest.detail.session.name', 'Session name')}  value={session.session_name ?? '—'} />
      <Cell label={t('pentest.detail.session.external', 'External id')} value={session.external_session_id ?? '—'} />
      <Cell label={t('pentest.detail.session.started', 'Started')}    value={fmtDateTime(session.started_at)} />
      <Cell label={t('pentest.detail.session.ended', 'Ended')}        value={fmtDateTime(session.ended_at)} />
      <Cell label={t('pentest.detail.session.nic', 'Source NIC')}     value={session.source_nic ?? '—'} />
      <Cell label={t('pentest.detail.session.submitted', 'Submitted')} value={fmtDateTime(session.submitted_at)} />
      {subnets && (
        <div className="sm:col-span-2">
          <Cell label={t('pentest.detail.session.subnets', 'Selected subnets')} value={
            <span className="font-mono text-[11px]">{subnets}</span>
          } />
        </div>
      )}
    </div>
  )
}

const SeverityBar: React.FC<{ sevCounts: Record<string, number>; total: number }> = ({ sevCounts, total }) => {
  const denom = Math.max(1, total)
  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded">
        {SEV_LEVELS.map(s => sevCounts[s] > 0 && (
          <div key={s} className={sevTone(s).dot} style={{ width: `${(sevCounts[s] / denom) * 100}%` }} title={`${s}: ${sevCounts[s]}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-600 dark:text-slate-300">
        {SEV_LEVELS.map(s => (
          <span key={s} className="inline-flex items-center gap-1">
            <span className={`inline-block w-2 h-2 rounded-full ${sevTone(s).dot}`} />
            <b>{sevCounts[s]}</b> {s.toLowerCase()}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── Assets tab ─────────────────────────────────────────────────────────
// combines the old Topology and Hosts tabs under a compact sub-mode
// selector. default mode is Topology because the visual subnet view is the
// most useful at-a-glance. "Hosts" gives the table
const AssetsTab: React.FC<{
  mode: AssetsMode
  onChangeMode: (m: AssetsMode) => void
  detail: EngagementDetail
  displayDetail: EngagementDetail | null
  portsByAsset: Map<string, EngagementPort[]>
  attackedIps: Set<string>
  isFullscreen: boolean
  onToggleFullscreen: () => void
  onNodeSelect: (data: Record<string, any> | null) => void
  canvasRef: React.MutableRefObject<TopologyViewHandle | null>
}> = ({ mode, onChangeMode, detail, displayDetail, portsByAsset, attackedIps,
       isFullscreen, onToggleFullscreen, onNodeSelect, canvasRef }) => {
  const { t } = useTranslation()
  const baseAssets = detail.assets.length > 0
    ? detail.assets
    : deriveAssetsFromTopology(detail.topology_snapshot)
  const topologyAssets = displayDetail?.assets ?? baseAssets
  return (
    <div className="relative w-full">
      <div className="mx-auto max-w-[1400px] px-0 mb-3 flex items-center gap-1">
        <SubModeButton active={mode === 'topology'} onClick={() => onChangeMode('topology')}>
          {t('pentest.detail.assets.topology', 'Topology')}
        </SubModeButton>
        <SubModeButton active={mode === 'hosts'} onClick={() => onChangeMode('hosts')}>
          {t('pentest.detail.assets.hosts', 'Hosts')}
          {topologyAssets.length > 0 && <span className="ml-1.5 text-[10px] text-slate-400">({topologyAssets.length})</span>}
        </SubModeButton>
      </div>
      {mode === 'topology' ? (
        <TopologyTab
          snapshot={detail.topology_snapshot}
          assets={topologyAssets}
          ports={detail.ports}
          subnets={detail.subnets}
          attackRuns={detail.attack_runs}
          findings={displayDetail?.findings ?? detail.findings}
          evidence={displayDetail?.evidence ?? detail.evidence}
          isFullscreen={isFullscreen}
          onToggleFullscreen={onToggleFullscreen}
          onNodeSelect={onNodeSelect}
          canvasRef={canvasRef}
        />
      ) : (
        <div className="mx-auto max-w-[1400px]">
          <HostsSection
            assets={baseAssets}
            portsByAsset={portsByAsset}
            topologySnapshot={detail.topology_snapshot}
            attackedIps={attackedIps}
            findings={displayDetail?.findings ?? detail.findings}
            evidence={displayDetail?.evidence ?? detail.evidence}
            onSelectFinding={onNodeSelect}
          />
        </div>
      )}
    </div>
  )
}

// pill-style sub-mode selector used inside Assets and Evidence tabs
const SubModeButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
      active
        ? 'bg-blue-50 text-blue-700 border border-blue-200'
        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
    }`}
  >
    {children}
  </button>
)

// ── Evidence tab ───────────────────────────────────────────────────────
// folds Findings + Evidence. a two-button chip filter swaps which list is
// showing. both call the same drawer so workflow doesn't change
const EvidenceWorkspace: React.FC<{
  mode: EvidenceMode
  onChangeMode: (m: EvidenceMode) => void
  findings: EngagementFinding[]
  evidence: EngagementEvidence[]
  subnets: EngagementDetail['subnets']
  assets: EngagementDetail['assets']
  onSelect: (data: Record<string, any> | null) => void
}> = ({ mode, onChangeMode, findings, evidence, subnets, assets, onSelect }) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1">
        <SubModeButton active={mode === 'findings'} onClick={() => onChangeMode('findings')}>
          {t('pentest.detail.evidence.findings', 'Confirmed findings')}
          {findings.length > 0 && <span className="ml-1.5 text-[10px] text-slate-400">({findings.length})</span>}
        </SubModeButton>
        <SubModeButton active={mode === 'evidence'} onClick={() => onChangeMode('evidence')}>
          {t('pentest.detail.evidence.items', 'Evidence items')}
          {evidence.length > 0 && <span className="ml-1.5 text-[10px] text-slate-400">({evidence.length})</span>}
        </SubModeButton>
      </div>
      {mode === 'findings'
        ? <FindingsSection findings={findings} subnets={subnets} assets={assets} onSelect={onSelect} />
        : <EvidenceSection evidence={evidence} assets={assets} onSelect={onSelect} />}
    </div>
  )
}

const TopologyTab: React.FC<{
  snapshot: EngagementDetail['topology_snapshot']
  assets: EngagementDetail['assets']
  ports: EngagementDetail['ports']
  subnets: EngagementDetail['subnets']
  attackRuns: EngagementDetail['attack_runs']
  findings: EngagementDetail['findings']
  evidence: EngagementDetail['evidence']
  isFullscreen: boolean
  onToggleFullscreen: () => void
  onNodeSelect: (nodeData: Record<string, any> | null) => void
  canvasRef: React.MutableRefObject<TopologyViewHandle | null>
}> = ({ snapshot, assets, ports, subnets, attackRuns, findings, evidence, isFullscreen, onToggleFullscreen, onNodeSelect, canvasRef }) => {
  // default to "all", the host grid is the most actionable surface inside
  // Assets > Topology. Summary is a roll-up the Overview already covers.
  // Subnet is kept available but isn't the default
  const [mode, setMode] = useState<TopologyMode>('all')

  const containerClass = isFullscreen
    ? 'fixed inset-0 z-40 bg-white p-4'
    : 'relative w-full'
  const canvasHeight = isFullscreen
    ? 'calc(100vh - 80px)'
    : 'calc(100vh - 220px)'

  // Summary / Subnet / All Hosts render in HTML, no zoom/fit affordance
  // applies. CanvasToolbar only shows in Attack Path mode where the
  // Cytoscape canvas backs the view
  const showCanvasToolbar = mode === 'attack'

  return (
    <div className={containerClass}>
      {!isFullscreen && (
        <div className="mb-3 flex items-center justify-between">
          <TopologyToolbar mode={mode} onChange={setMode} />
        </div>
      )}

      <div className="relative w-full" style={{ height: canvasHeight }}>
        <TopologyView
          ref={node => { canvasRef.current = node }}
          snapshot={snapshot}
          assets={assets}
          ports={ports}
          subnets={subnets}
          attackRuns={attackRuns}
          findings={findings}
          evidence={evidence}
          mode={mode}
          onSelect={onNodeSelect}
        />
        {showCanvasToolbar && (
          <CanvasToolbar
            onZoomIn={() => canvasRef.current?.zoomIn()}
            onZoomOut={() => canvasRef.current?.zoomOut()}
            onFit={() => canvasRef.current?.fit()}
            onToggleFullscreen={onToggleFullscreen}
            isFullscreen={isFullscreen}
          />
        )}
      </div>
    </div>
  )
}

// simulations table, formerly "Attack runs", reframed as "Simulation runs"
// so the operator reads each row as evidence of what was generated, not a
// verdict on the target. rows open a bottom sheet detail view. the
// "Observed reaction" column is a placeholder until Dorothy ships an
// observation field (TODO: wire to editable note)
const AttacksSection: React.FC<{
  attackRuns:   EngagementDetail['attack_runs']
  engagementId: string
  onSelect: (data: Record<string, any> | null) => void
}> = ({ attackRuns, engagementId, onSelect }) => {
  const { t } = useTranslation()
  if (attackRuns.length === 0) {
    return (
      <section className="rounded-lg bg-slate-50 border border-slate-200 p-3">
        <h2 className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
          {t('pentest.detail.simulations.title', 'Simulation runs')} (0)
        </h2>
        <p className="text-xs text-slate-500">
          {t('pentest.detail.simulations.empty', 'No simulation runs captured for this engagement.')}
        </p>
      </section>
    )
  }
  const fmt = (iso: string | null) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
  }
  const fmtNum = (n: number | null | undefined): string =>
    n == null ? '—' : new Intl.NumberFormat().format(Math.round(n))
  // avg PPS resolution order (migration 018):
  //   1. Dorothy `average_pps` on the row (real measurement)
  //   2. fallback to packets / duration when both are present
  //   3. null → cell shows "—"
  // Peak / Target NEVER get faked, only shown when Dorothy reported them.
  // the placeholder dash is deliberate so the operator doesn't mistake an
  // interpolation for a measurement
  const avgPpsFor = (r: EngagementDetail['attack_runs'][number]): number | null => {
    if (r.average_pps != null && isFinite(r.average_pps)) return r.average_pps
    if (r.packets_sent != null && r.duration_seconds != null && r.duration_seconds > 0) {
      return Math.round(r.packets_sent / r.duration_seconds)
    }
    return null
  }
  // manual observation extractor. SEACUREDB column observed_reaction
  // (migration 019) is the canonical source. older fallback names cover
  // any inflight Dorothy submissions that pre-shipped one
  const observed = (r: any): string | null =>
    r.observed_reaction ?? r.observed_response ?? r.manual_note ?? r.observation ?? null

  return (
    <section className="rounded-lg bg-white border border-slate-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-baseline justify-between">
        <h2 className="text-[10px] uppercase tracking-wide text-slate-400">
          {t('pentest.detail.simulations.title', 'Simulation runs')} ({attackRuns.length})
        </h2>
        <span className="text-[10px] text-slate-400 italic">
          {t('pentest.detail.simulations.subtitle', 'What was sent — not a verdict on the target')}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-500">
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.simulations.col.protocol', 'Protocol')}</th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.simulations.col.type',     'Simulation type')}</th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.simulations.col.target',   'Target')}</th>
              <th className="px-2 py-1.5 font-medium text-right">{t('pentest.detail.simulations.col.rateMbps', 'Configured Mbps')}</th>
              <th className="px-2 py-1.5 font-medium text-right">{t('pentest.detail.simulations.col.packets',  'Packets sent')}</th>
              <th className="px-2 py-1.5 font-medium text-right">{t('pentest.detail.simulations.col.duration', 'Duration (s)')}</th>
              <th className="px-2 py-1.5 font-medium text-right">{t('pentest.detail.simulations.col.avgPps',   'Avg PPS')}</th>
              <th className="px-2 py-1.5 font-medium text-right">{t('pentest.detail.simulations.col.peakPps',  'Peak PPS')}</th>
              <th className="px-2 py-1.5 font-medium text-right">{t('pentest.detail.simulations.col.targetPps','Target PPS')}</th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.simulations.col.start',    'Start')}</th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.simulations.col.stop',     'Stop')}</th>
              <th className="px-2 py-1.5 font-medium">{t('pentest.detail.simulations.col.observed', 'Observed reaction')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {attackRuns.map(r => {
              const avgPps = avgPpsFor(r)
              const note   = observed(r)
              // resolution policy: calculated avg gets a slightly dimmer
              // tone so the operator can tell "Dorothy measurement" from
              // "client-side derivation"
              const avgFromDorothy = r.average_pps != null && isFinite(r.average_pps)
              return (
                <tr
                  key={r.id}
                  onClick={() => onSelect({
                    kind: 'attack',
                    id:               r.id,
                    engagement_id:    engagementId,
                    run_id:           r.id,
                    attack_type:      r.attack_type,
                    protocol:         r.protocol,
                    target:           r.target_ip,
                    target_port:      r.target_port,
                    target_rate_mbps: r.target_rate_mbps,
                    packets_sent:     r.packets_sent,
                    duration_seconds: r.duration_seconds,
                    start_time:       r.start_time,
                    stop_time:        r.stop_time,
                    current_pps:      r.current_pps ?? null,
                    average_pps:      r.average_pps ?? null,
                    peak_pps:         r.peak_pps ?? null,
                    target_pps:       r.target_pps ?? null,
                    approx_pps:       avgPps,
                    avg_pps_source:   avgFromDorothy ? 'dorothy' : (avgPps != null ? 'calculated' : 'missing'),
                    observed:         note,
                    observed_reaction: r.observed_reaction ?? null,
                    reviewed_by:       r.reviewed_by ?? null,
                    reviewed_at:       r.reviewed_at ?? null,
                    source_tool:      (r as any).source_tool ?? null,
                  })}
                  className="hover:bg-slate-50 cursor-pointer"
                >
                  <td className="px-2 py-1.5">{r.protocol ?? '—'}</td>
                  <td className="px-2 py-1.5">{r.attack_type ?? '—'}</td>
                  <td className="px-2 py-1.5 font-mono">
                    {r.target_ip ?? '—'}
                    {r.target_port != null && <span className="text-slate-400">:{r.target_port}</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{r.target_rate_mbps ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmtNum(r.packets_sent)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{r.duration_seconds ?? '—'}</td>
                  <td
                    className={`px-2 py-1.5 text-right tabular-nums ${avgFromDorothy ? 'text-slate-700' : 'text-slate-400'}`}
                    title={avgFromDorothy ? 'Reported by Dorothy' : 'Approximated from packets ÷ duration'}
                  >
                    {fmtNum(avgPps)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{fmtNum(r.peak_pps)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{fmtNum(r.target_pps)}</td>
                  <td className="px-2 py-1.5">{fmt(r.start_time)}</td>
                  <td className="px-2 py-1.5">{fmt(r.stop_time)}</td>
                  <td className="px-2 py-1.5 text-slate-500 truncate max-w-xs" title={note ?? 'Not recorded'}>
                    {note ?? <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 border-t border-slate-100 text-[10px] text-slate-400 italic">
        {t('pentest.detail.simulations.observed.todo',
          'Observed reaction is captured manually. Dorothy does not yet emit per-run impact data — add notes during review.')}
      </div>
    </section>
  )
}

// ── Findings tab ────────────────────────────────────────────────────────

const FindingsSection: React.FC<{
  findings: EngagementFinding[]
  subnets:  EngagementDetail['subnets']
  assets:   EngagementDetail['assets']
  onSelect: (data: Record<string, any> | null) => void
}> = ({ findings, subnets, assets, onSelect }) => {
  const { t } = useTranslation()
  const [sevFilter,    setSevFilter]    = useState<string>(ALL)
  const [subnetFilter, setSubnetFilter] = useState<string>(ALL)
  const [hostFilter,   setHostFilter]   = useState<string>(ALL)
  const [statusFilter, setStatusFilter] = useState<string>(ALL)

  const filtered = useMemo(() => findings.filter(f => {
    if (sevFilter    !== ALL && f.severity.toUpperCase() !== sevFilter) return false
    if (statusFilter !== ALL && (f.status ?? 'open').toLowerCase() !== statusFilter) return false
    const ip     = f.asset?.host_ip ?? null
    const cidr   = f.asset?.subnet_cidr ?? (ip ? subnetOf(ip) : null)
    if (subnetFilter !== ALL && cidr !== subnetFilter) return false
    if (hostFilter   !== ALL && ip !== hostFilter)     return false
    return true
  }), [findings, sevFilter, subnetFilter, hostFilter, statusFilter])

  const subnetOpts = useMemo(() => {
    const s = new Set<string>()
    for (const sub of subnets) s.add(sub.cidr)
    for (const f of findings) {
      const cidr = f.asset?.subnet_cidr ?? (f.asset?.host_ip ? subnetOf(f.asset.host_ip) : null)
      if (cidr) s.add(cidr)
    }
    return Array.from(s).sort()
  }, [subnets, findings])

  const hostOpts = useMemo(() => {
    const s = new Set<string>()
    for (const a of assets) s.add(a.host_ip)
    for (const f of findings) if (f.asset?.host_ip) s.add(f.asset.host_ip)
    return Array.from(s).sort()
  }, [assets, findings])

  if (findings.length === 0) {
    return (
      <section className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-center">
        <AlertTriangle className="h-6 w-6 text-slate-300 mx-auto mb-1.5" />
        <p className="text-xs text-slate-500">
          {t('pentest.detail.findings.empty', 'No findings submitted yet.')}
        </p>
      </section>
    )
  }

  const SelectCls = 'text-[11px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5'

  return (
    <section className="rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
      <header className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-2 justify-between bg-slate-50 dark:bg-slate-800/50">
        <h2 className="text-[10px] uppercase tracking-wide text-slate-400">
          {t('pentest.detail.findings.title', 'Findings')} ({filtered.length}{filtered.length !== findings.length ? ` / ${findings.length}` : ''})
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[10px] text-slate-400 inline-flex items-center gap-1">
            {t('pentest.detail.findings.filter.severity', 'Severity')}
            <select className={SelectCls} value={sevFilter} onChange={e => setSevFilter(e.target.value)}>
              <option value={ALL}>All</option>
              {SEV_LEVELS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          {subnetOpts.length > 1 && (
            <label className="text-[10px] text-slate-400 inline-flex items-center gap-1">
              {t('pentest.detail.findings.filter.subnet', 'Subnet')}
              <select className={SelectCls} value={subnetFilter} onChange={e => setSubnetFilter(e.target.value)}>
                <option value={ALL}>All</option>
                {subnetOpts.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          )}
          <label className="text-[10px] text-slate-400 inline-flex items-center gap-1">
            {t('pentest.detail.findings.filter.host', 'Host')}
            <select className={SelectCls} value={hostFilter} onChange={e => setHostFilter(e.target.value)}>
              <option value={ALL}>All</option>
              {hostOpts.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </label>
          <label className="text-[10px] text-slate-400 inline-flex items-center gap-1">
            {t('pentest.detail.findings.filter.status', 'Status')}
            <select className={SelectCls} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value={ALL}>All</option>
              <option value="open">open</option>
              <option value="closed">closed</option>
              <option value="in_progress">in_progress</option>
            </select>
          </label>
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-slate-900/40">
            <tr className="text-left text-slate-500 dark:text-slate-400">
              <th className="px-2 py-1.5 font-medium w-24">Severity</th>
              <th className="px-2 py-1.5 font-medium">Title</th>
              <th className="px-2 py-1.5 font-medium">Affected host</th>
              <th className="px-2 py-1.5 font-medium">Category</th>
              <th className="px-2 py-1.5 font-medium">Status</th>
              <th className="px-2 py-1.5 font-medium text-right">Evidence</th>
              <th className="px-2 py-1.5 font-medium">Recommendation</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
            {filtered.map(f => {
              const tone = sevTone(f.severity)
              const evCount = Array.isArray(f.evidence_ids) ? f.evidence_ids.length : 0
              return (
                <tr key={f.id}
                    onClick={() => onSelect({ kind: 'finding', ...f })}
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/40 cursor-pointer">
                  <td className="px-2 py-1.5">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${tone.pill}`}>
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                      {f.severity}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-slate-700 dark:text-slate-200">{f.title}</td>
                  <td className="px-2 py-1.5">
                    {(() => {
                      const ip = f.asset?.host_ip ?? findingHostIp(f, [])
                      if (ip) {
                        return (
                          <span>
                            <span className="font-mono text-[11px]">{ip}</span>
                            {f.asset?.host_name && <span className="text-slate-500 ml-1">{f.asset.host_name}</span>}
                            {f.asset?.subnet_cidr && <span className="text-slate-400 ml-1 text-[10px]">({f.asset.subnet_cidr})</span>}
                          </span>
                        )
                      }
                      if (f.external_asset_id) {
                        return <span className="text-slate-400 font-mono text-[10px]">{f.external_asset_id}</span>
                      }
                      return <span className="text-slate-400">—</span>
                    })()}
                  </td>
                  <td className="px-2 py-1.5">{f.category ?? '—'}</td>
                  <td className="px-2 py-1.5 text-slate-500">{f.status}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{evCount}</td>
                  <td className="px-2 py-1.5 text-slate-500 truncate max-w-xs" title={f.recommendation ?? ''}>
                    {f.recommendation ? <span className="line-clamp-2">{f.recommendation}</span> : '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ── Evidence tab ────────────────────────────────────────────────────────

const EvidenceSection: React.FC<{
  evidence: EngagementEvidence[]
  assets:   EngagementDetail['assets']
  onSelect: (data: Record<string, any> | null) => void
}> = ({ evidence, assets, onSelect }) => {
  const { t } = useTranslation()
  const [typeFilter, setTypeFilter] = useState<string>(ALL)
  const [hostFilter, setHostFilter] = useState<string>(ALL)
  const [sevFilter,  setSevFilter]  = useState<string>(ALL)

  const filtered = useMemo(() => evidence.filter(ev => {
    if (typeFilter !== ALL && ev.evidence_type !== typeFilter) return false
    if (hostFilter !== ALL && (ev.asset?.host_ip ?? '') !== hostFilter) return false
    if (sevFilter  !== ALL && (ev.severity_hint ?? '').toUpperCase() !== sevFilter) return false
    return true
  }), [evidence, typeFilter, hostFilter, sevFilter])

  const typeOpts = useMemo(() => Array.from(new Set(evidence.map(e => e.evidence_type))).sort(), [evidence])
  const hostOpts = useMemo(() => {
    const s = new Set<string>()
    for (const a of assets) s.add(a.host_ip)
    for (const ev of evidence) if (ev.asset?.host_ip) s.add(ev.asset.host_ip)
    return Array.from(s).sort()
  }, [assets, evidence])

  if (evidence.length === 0) {
    return (
      <section className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-center">
        <FileText className="h-6 w-6 text-slate-300 mx-auto mb-1.5" />
        <p className="text-xs text-slate-500">
          {t('pentest.detail.evidence.empty', 'No evidence submitted yet.')}
        </p>
      </section>
    )
  }

  const SelectCls = 'text-[11px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5'

  return (
    <section className="rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
      <header className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-2 justify-between bg-slate-50 dark:bg-slate-800/50">
        <h2 className="text-[10px] uppercase tracking-wide text-slate-400">
          {t('pentest.detail.evidence.title', 'Evidence')} ({filtered.length}{filtered.length !== evidence.length ? ` / ${evidence.length}` : ''})
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[10px] text-slate-400 inline-flex items-center gap-1">
            {t('pentest.detail.evidence.filter.type', 'Type')}
            <select className={SelectCls} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value={ALL}>All</option>
              {typeOpts.map(t => <option key={t} value={t}>{humanEvidenceType(t)}</option>)}
            </select>
          </label>
          <label className="text-[10px] text-slate-400 inline-flex items-center gap-1">
            {t('pentest.detail.evidence.filter.host', 'Host')}
            <select className={SelectCls} value={hostFilter} onChange={e => setHostFilter(e.target.value)}>
              <option value={ALL}>All</option>
              {hostOpts.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </label>
          <label className="text-[10px] text-slate-400 inline-flex items-center gap-1">
            {t('pentest.detail.evidence.filter.severity', 'Severity hint')}
            <select className={SelectCls} value={sevFilter} onChange={e => setSevFilter(e.target.value)}>
              <option value={ALL}>All</option>
              {SEV_LEVELS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-slate-900/40">
            <tr className="text-left text-slate-500 dark:text-slate-400">
              <th className="px-2 py-1.5 font-medium">Type</th>
              <th className="px-2 py-1.5 font-medium">Source tool</th>
              <th className="px-2 py-1.5 font-medium">Host</th>
              <th className="px-2 py-1.5 font-medium">Collected</th>
              <th className="px-2 py-1.5 font-medium">Severity</th>
              <th className="px-2 py-1.5 font-medium">Summary</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
            {filtered.map(ev => {
              const tone = ev.severity_hint ? sevTone(ev.severity_hint) : sevTone('')
              return (
                <tr key={ev.id}
                    onClick={() => onSelect({ kind: 'evidence', ...ev })}
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/40 cursor-pointer">
                  <td className="px-2 py-1.5 text-[11px]">{humanEvidenceType(ev.evidence_type)}</td>
                  <td className="px-2 py-1.5 text-slate-500">{ev.source_tool ?? '—'}</td>
                  <td className="px-2 py-1.5">
                    {(() => {
                      const ip = evidenceHostIp(ev)
                      const hostName = ev.asset?.host_name ?? null
                      if (ip) {
                        return (
                          <span>
                            <span className="font-mono text-[11px]">{ip}</span>
                            {hostName && <span className="text-slate-500 ml-1">{hostName}</span>}
                          </span>
                        )
                      }
                      if (ev.external_asset_id) {
                        return <span className="text-slate-400 font-mono text-[10px]">{ev.external_asset_id}</span>
                      }
                      return <span className="text-slate-400">—</span>
                    })()}
                  </td>
                  <td className="px-2 py-1.5 text-slate-500">{fmtDateTime(ev.collected_at)}</td>
                  <td className="px-2 py-1.5">
                    {ev.severity_hint ? (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${tone.pill}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                        {ev.severity_hint}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-slate-700 dark:text-slate-200 truncate max-w-md" title={ev.summary ?? ''}>
                    {serviceEvidenceKeySummary(ev) || snmpEvidenceKeySummary(ev) || (ev.summary ?? '—')}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
